import React, { useMemo, useState } from "react";
import { Box, Text, HStack, Icon, VStack, Collapse } from "@chakra-ui/react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, artifactTypeToJSON } from "build-ts/common";
import { getRandomActionVerb } from "utils/nameUtils";
import { CHAT_ACTION_VERBS } from "constants/messages";
import { MdOutlineTaskAlt } from "react-icons/md";

interface ArtifactsListProps {
	artifacts?: ArtifactProto[];
	langgraphMessageJsonList?: string[];
	onArtifactClick?: (artifact: ArtifactProto) => void;
}

// Helper function to convert artifact type to readable label
const getArtifactTypeLabel = (type?: ArtifactType): string => {
	if (!type) return "Unknown Type";
	if (type === ArtifactType.KG_OBSERVATION) {
		return "KG Observation";
	} else if (type === ArtifactType.KG_DATAFRAME) {
		return "KG DataFrame";
	} else if (type === ArtifactType.SCENARIO) {
		return "Scenario";
	} else {
		// Handle UNKNOWN_ARTIFACT_TYPE and any other cases
		return artifactTypeToJSON(type);
	}
};

const SCENARIO_TOOL_NAME_PATTERNS = [
	"update_analysis",
	"save_analysis",
	"get_analysis_detail",
];

const getScenarioStepLabel = (
	toolName: string | undefined,
	scenarioName: string | null | undefined
): { prefix: string; name: string } => {
	const name = scenarioName?.trim() || "scenario";
	const lower = (toolName ?? "").toLowerCase().trim();

	if (lower.includes("update")) {
		return { prefix: "Updated scenario", name };
	}
	if (lower.includes("save")) {
		return { prefix: "Saved scenario", name };
	}
	if (lower.includes("get") || lower.includes("detail")) {
		return { prefix: "Retrieved details for", name };
	}
	return { prefix: "Scenario", name };
};

const getScenarioNameFromArgs = (args: Record<string, unknown>): string | null => {
	if (!args) return null;
	return (
		(args.name as string) ||
		(args.scenario_name as string) ||
		(args.issue as string) ||
		(args.scenario_id ? `#${args.scenario_id}` : null) ||
		null
	);
};

const isScenarioToolName = (name: string): boolean => {
	const lower_name = name.toLowerCase().trim();
	return SCENARIO_TOOL_NAME_PATTERNS.some(p => lower_name.includes(p));
};

/** Extract scenario tool calls from langgraph messages for matching to artifacts (tool name + scenario name). */
const getScenarioToolSteps = (langgraphMessageJsonList: string[]): { toolName: string; scenarioName: string | null }[] => {
	const steps: { toolName: string; scenarioName: string | null }[] = [];
	for (const jsonString of langgraphMessageJsonList) {
		try {
			const parsed = JSON.parse(jsonString);
			if (parsed.type === "ai" && parsed.tool_calls && Array.isArray(parsed.tool_calls)) {
				for (const toolCall of parsed.tool_calls) {
					if (toolCall.name && isScenarioToolName(toolCall.name)) {
						const args = toolCall.args || {};
						steps.push({
							toolName: toolCall.name,
							scenarioName: getScenarioNameFromArgs(args),
						});
					}
				}
			}
		} catch {
			// Ignore parse errors
		}
	}
	return steps;
};

const ArtifactsList: React.FC<ArtifactsListProps> = ({
	artifacts,
	langgraphMessageJsonList,
	onArtifactClick,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const hasLanggraphMessages = langgraphMessageJsonList && langgraphMessageJsonList.length > 0;

	// Scenario tool steps from langgraph messages (for Updated/Saved/Retrieved label)
	const scenarioToolSteps = useMemo(
		() => (hasLanggraphMessages ? getScenarioToolSteps(langgraphMessageJsonList!) : []),
		[hasLanggraphMessages, langgraphMessageJsonList]
	);

	// Scenario artifacts from the message — only show scenario rows when these exist
	const scenarioArtifacts = (artifacts ?? []).filter(
		a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO
	) as Array<ArtifactProto & { scenario?: { issue?: string } }>;
	const hasScenarioArtifacts = scenarioArtifacts.length > 0;

	// One row per scenario artifact; display uses scenario name (issue) with Updated/Saved/Retrieved when we can match a tool step
	const scenarioDisplayNames = useMemo(() => {
		if (!hasScenarioArtifacts) return [];
		return scenarioArtifacts.map(
			sa => (sa.scenario?.issue && String(sa.scenario.issue).trim()) || "Scenario"
		);
	}, [hasScenarioArtifacts, scenarioArtifacts]);
	const scenarioRowCount = scenarioDisplayNames.length;

	// Non-scenario artifacts we want to show as generic "table" analysis steps.
	const nonScenarioArtifacts = (artifacts ?? []).filter(a => {
		const t = artifactTypeFromJSON(a.artifact_type);
		return t === ArtifactType.KG_DATAFRAME || t === ArtifactType.KG_OBSERVATION;
	});

	// SCENARIO_JOB artifacts shown as clickable rows
	const jobArtifacts = (artifacts ?? []).filter(
		a => artifactTypeFromJSON(a.artifact_type) === ArtifactType.SCENARIO_JOB
	);

	const hasNonScenarioArtifacts = nonScenarioArtifacts.length > 0;
	const hasAnyContent = scenarioRowCount > 0 || hasNonScenarioArtifacts || jobArtifacts.length > 0;

	if (!hasAnyContent) {
		return null;
	}

	const totalCount = scenarioRowCount + nonScenarioArtifacts.length + jobArtifacts.length;

	return (
		<Box
			bg="gray.50"
			borderRadius="md"
			width="100%"
			m={0}
			overflow="hidden"
			border="1px solid"
			borderColor="gray.300"
		>
			{/* Collapsible Header */}
			<HStack
				spacing={1}
				align="center"
				p={1}
				px={2}
				cursor="pointer"
				onClick={() => setIsExpanded(!isExpanded)}
				_hover={{ bg: "gray.100" }}
				transition="background 0.2s"
			>
				<Text fontSize="sm" color="gray.700" fontWeight="600" mb={0}>
					Analysis Steps
				</Text>
				<Text fontSize="sm" color="gray.700" mb={0} fontWeight="400">
					({totalCount} step{totalCount !== 1 ? "s" : ""})
				</Text>
				<Icon
					as={isExpanded ? FaChevronDown : FaChevronRight}
					boxSize={3}
					color="gray.600"
					ml={1}
				/>
			</HStack>

			{/* Collapsible Content */}
			<Collapse in={isExpanded} animateOpacity>
				<VStack spacing={0} align="flex-start" px={3} w="100%">
					{/* Scenario rows: only when scenario artifacts exist; show Updated/Saved/Retrieved using matched tool step */}
					{scenarioDisplayNames.map((scenarioName, index) => {
						const scenarioArtifactForRow = scenarioArtifacts[index];
						const issue = scenarioArtifactForRow?.scenario?.issue?.trim();
						// Match by name first (e.g. update_scenario_metadata with "name" in args)
						let matchingStep = scenarioToolSteps.find(
							step => step.scenarioName?.trim().toLowerCase() === (issue ?? "").toLowerCase()
						);
						// When tool args only have draft_scenario_id (e.g. save_scenario with id "4"),
						// scenarioName is "#4" and won't match artifact issue; fall back to same order
						if (!matchingStep && index < scenarioToolSteps.length) {
							matchingStep = scenarioToolSteps[index];
						}
						const label = getScenarioStepLabel(matchingStep?.toolName, scenarioName);
						const isClickable = !!onArtifactClick && !!scenarioArtifactForRow;
						return (
							<Box
								key={`scenario-${index}`}
								w="100%"
								py={1}
								bg="transparent"
								cursor={isClickable ? "pointer" : undefined}
								onClick={isClickable ? () => onArtifactClick?.(scenarioArtifactForRow) : undefined}
								_hover={isClickable ? { bg: "gray.100", borderRadius: "md" } : undefined}
							>
								<HStack spacing={2} align="center">
									<Icon as={MdOutlineTaskAlt} boxSize={4} color="gray.700" mr={1} />
									<Text fontSize="sm" color="gray.600" fontWeight="500" mb={0} textAlign="left">
										{label.prefix}{" "}
										<Text as="span" fontWeight="semibold" color="gray.900">
											{label.name}
										</Text>
									</Text>
								</HStack>
							</Box>
						);
					})}
					{/* Non-scenario artifacts (dataframes, observations, etc.) */}
					{nonScenarioArtifacts.map((artifact, index) => {
						return (
							<Box
								key={index}
								w="100%"
								py={1}
								bg="transparent"
								cursor="pointer"
								onClick={() => onArtifactClick?.(artifact)}
								_hover={{
									bg: "gray.100",
									borderRadius: "md",
								}}
							>
								<HStack spacing={2} align="center">
									<Icon as={MdOutlineTaskAlt} boxSize={4} color="gray.700" mr={1} />
									<Text fontSize="sm" color="gray.600" fontWeight="500" mb={0} textAlign="left">
										{getRandomActionVerb(CHAT_ACTION_VERBS.analyzed, artifact?.kg_dataframe?.header?.name || "", index)}{" "}
										<Text as="span" fontWeight="semibold" color="gray.900">
											{artifact.artifact_type === ArtifactType.KG_DATAFRAME
												? artifact.kg_dataframe?.header?.name
												: getArtifactTypeLabel(artifactTypeFromJSON(artifact.artifact_type))}
										</Text>
										{" "} table
									</Text>
								</HStack>
							</Box>
						);
					})}
					{/* SCENARIO_JOB artifacts — same label logic as scenario rows, name from header.name */}
					{jobArtifacts.map((artifact, index) => {
						const jobName = artifact.scenario_job?.header?.name?.trim() || "New Job";
						let matchingStep = scenarioToolSteps.find(
							step => step.scenarioName?.trim().toLowerCase() === jobName.toLowerCase()
						);
						if (!matchingStep && index < scenarioToolSteps.length) {
							matchingStep = scenarioToolSteps[index];
						}
						const label = getScenarioStepLabel(matchingStep?.toolName, jobName);
						const isClickable = !!onArtifactClick;
						return (
							<Box
								key={`job-${index}`}
								w="100%"
								py={1}
								bg="transparent"
								cursor={isClickable ? "pointer" : undefined}
								onClick={isClickable ? () => onArtifactClick?.(artifact) : undefined}
								_hover={isClickable ? { bg: "gray.100", borderRadius: "md" } : undefined}
							>
								<HStack spacing={2} align="center">
									<Icon as={MdOutlineTaskAlt} boxSize={4} color="gray.700" mr={1} />
									<Text fontSize="sm" color="gray.600" fontWeight="500" mb={0} textAlign="left">
										{label.prefix}{" "}
										<Text as="span" fontWeight="semibold" color="gray.900">
											{label.name}
										</Text>
									</Text>
								</HStack>
							</Box>
						);
					})}
				</VStack>
			</Collapse>
		</Box>
	);
};

export default ArtifactsList; 